/**
 * Website analytics, read through spark-creatives.
 *
 * The brand's own website (and its landing pages) send anonymous visit events to Heimdall; they are
 * stored per address and read back through the analytics service. The owner never talks to that service:
 * spark-creatives checks that the signed-in owner owns the brand, works out the brand's addresses itself
 * and only ever asks for those (`website_id in <the brand's hosts>`), so the queries below can narrow the
 * data but never widen it to another brand.
 *
 *   GET  {creatives}/creatives/tenant/{tenant}/website-analytics/hosts      → the brand's tracked addresses
 *   POST {creatives}/creatives/tenant/{tenant}/website-analytics/aggregate  → one aggregate query
 *
 * The aggregate body is the analytics service's own `/v1/aggregate` request minus the dataset and the
 * address filter (both forced server-side); the answer carries that service's response verbatim.
 */
import axios from 'axios';

import { creativesRequestConfig, creativesServiceBase, creativesServiceConfigured } from '../creatives/creative-api';

/** Where an address belongs: the brand's website or its landing pages (`lp.` hosts). */
export type HostKind = 'site' | 'landing';

export type AnalyticsHost = { host: string; kind: HostKind };

export type AnalyticsHosts = {
  /** False when the service cannot reach analytics at all (not set up in this environment). */
  configured: boolean;
  /** True when this environment answers attributed queries (visits credited to a source or a link). */
  attribution: boolean;
  conditions: boolean;
  /** True when some of the brand's addresses could not be read: the list may be incomplete, so a saved host missing
   *  from it is not known to be gone. */
  degraded: boolean;
  hosts: AnalyticsHost[];
};

export type FilterValue = string | number | boolean;

export type AnalyticsFilter = {
  field: string;
  op: 'eq' | 'ne' | 'in' | 'not_in' | 'contains' | 'starts_with' | 'ends_with' | 'exists' | 'missing';
  value?: FilterValue | FilterValue[];
};

export type AnalyticsMetric = {
  /** Output column name. */
  name: string;
  op: 'count' | 'sessions' | 'visitors' | 'sum' | 'avg' | 'min' | 'max';
  /** Numeric field for sum/avg/min/max. */
  field?: string;
};

export type TimeBucket = 'hour' | 'day' | 'week';

/**
 * What an attributed query counts, each credited to its latest non-direct source within 30 days: one row per visitor
 * (`visitor`), per visit (`session`), or every event at its own time (`event`). spark-creatives adds the brand's
 * addresses and the environment.
 */
export type AttributionUnit = 'visitor' | 'session' | 'event';

export type ConditionGroup = { conditions: AnalyticsFilter[]; joins?: ('AND' | 'OR')[] };
export type AnalyticsConditions = { groups: ConditionGroup[]; joins?: ('AND' | 'OR')[] };

export type AggregateQuery = {
  from: string;
  to: string;
  metrics: AnalyticsMetric[];
  groupBy?: string[];
  filters?: AnalyticsFilter[];
  conditions?: AnalyticsConditions;
  timeBucket?: TimeBucket;
  orderBy?: { field: string; direction: 'asc' | 'desc' };
  limit?: number;
  /** A subset of the brand's addresses; omitted means all of them. */
  hosts?: string[];
  /** Count attributed visits (see {@link AttributionUnit}); omitted: every event as it was recorded. */
  attribution?: { unit: AttributionUnit };
};

/** One answer row: column name → value. Counts can arrive as decimal strings. */
export type AnalyticsRow = Record<string, string | number | boolean | null>;

export type AggregateAnswer = {
  hosts: string[];
  rows: AnalyticsRow[];
};

/** The service answered with a refusal or failure; `status` is the HTTP status (0 = no answer). */
export class AnalyticsError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'AnalyticsError';
    this.status = status;
  }
}

export const analyticsConfigured = creativesServiceConfigured;

const routeBase = (tenantId: number): string =>
  `${creativesServiceBase()}/creatives/tenant/${encodeURIComponent(String(tenantId))}/website-analytics`;

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

const errorText = (data: unknown, status: number): string => {
  if (isRecord(data)) {
    if (typeof data.error === 'string' && data.error) return data.error;
    if (typeof data.message === 'string' && data.message) return data.message;
  }
  return `HTTP ${status}`;
};

/** A 2xx answer that is not a JSON object (a SPA fallback page when the dev proxy or the service is down) is an error,
 *  not "no website" or "no visits". */
const answerBody = (res: { status: number; data: unknown }): Record<string, unknown> => {
  if (res.status < 200 || res.status >= 300) throw new AnalyticsError(errorText(res.data, res.status), res.status);
  if (!isRecord(res.data)) throw new AnalyticsError(`unexpected response (HTTP ${res.status})`, res.status);
  return res.data;
};

const toHost = (v: unknown): AnalyticsHost | null => {
  if (!isRecord(v) || typeof v.host !== 'string' || !v.host) return null;
  return { host: v.host, kind: v.kind === 'landing' ? 'landing' : 'site' };
};

/** The brand's tracked addresses. */
export const fetchAnalyticsHosts = async (tenantId: number, signal?: AbortSignal): Promise<AnalyticsHosts> => {
  const res = await axios.get<unknown>(`${routeBase(tenantId)}/hosts`, creativesRequestConfig(signal));
  const data = answerBody(res);
  const hosts = Array.isArray(data.hosts) ? data.hosts.map(toHost).filter((h): h is AnalyticsHost => h !== null) : [];
  return {
    configured: data.configured !== false,
    attribution: data.attribution === true,
    conditions: data.conditions === true,
    degraded: data.degraded === true,
    hosts,
  };
};

const toRow = (v: unknown): AnalyticsRow | null => {
  if (!isRecord(v)) return null;
  const row: AnalyticsRow = {};
  Object.entries(v).forEach(([k, value]) => {
    row[k] = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? value : null;
  });
  return row;
};

/** Run one aggregate query over the brand's addresses. A busy service (503) is retried once. */
export const runAggregate = async (
  tenantId: number,
  query: AggregateQuery,
  signal?: AbortSignal,
): Promise<AggregateAnswer> => {
  const attempt = async (): Promise<AggregateAnswer> => {
    const res = await axios.post<unknown>(`${routeBase(tenantId)}/aggregate`, query, creativesRequestConfig(signal));
    const data = answerBody(res);
    const hosts = Array.isArray(data.hosts) ? data.hosts.filter((h): h is string => typeof h === 'string') : [];
    const result = isRecord(data.result) ? data.result : {};
    const rows = Array.isArray(result.rows) ? result.rows.map(toRow).filter((r): r is AnalyticsRow => r !== null) : [];
    return { hosts, rows };
  };
  try {
    return await attempt();
  } catch (err) {
    if (err instanceof AnalyticsError && err.status === 503 && !signal?.aborted) {
      await new Promise((resolve) => window.setTimeout(resolve, 1200));
      return attempt();
    }
    throw err;
  }
};
